import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  CreateMatchEventInput,
  MatchEventType,
  MatchStatus,
  MatchSummaryDto,
  useAddMatchEvent,
  useCreateMatch,
  useDeleteMatch,
  useMatch,
  useMatches,
  useNextMatch,
  useUpdateMatch,
} from '@shared/api/hooks/useMatches';
import { usePartners } from '@shared/api/hooks/usePartners';
import { MediaOrUrlField } from '@shared/components/MediaOrUrlField';

const STATUS_COLORS: Record<MatchStatus, string> = {
  SCHEDULED: 'default',
  LIVE: 'red',
  FINISHED: 'green',
  POSTPONED: 'gold',
  CANCELLED: 'volcano',
};

const STATUS_LABELS: Record<MatchStatus, string> = {
  SCHEDULED: 'Schedulata',
  LIVE: 'Live',
  FINISHED: 'Conclusa',
  POSTPONED: 'Rinviata',
  CANCELLED: 'Annullata',
};

const EVENT_LABELS: Record<MatchEventType, string> = {
  GOAL: 'Gol',
  YELLOW_CARD: 'Giallo',
  RED_CARD: 'Rosso',
  SUBSTITUTION: 'Cambio',
  PENALTY_SCORED: 'Rigore segnato',
  PENALTY_MISSED: 'Rigore sbagliato',
  OWN_GOAL: 'Autogol',
  VAR_DECISION: 'VAR',
};

const ROME_TZ = 'Europe/Rome';

interface CreateValues {
  homeTeamName: string;
  awayTeamName: string;
  isHome: boolean;
  competition: string;
  matchday?: number;
  date: Dayjs;
  venue: string;
  ticketUrl?: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  presentingPartnerId?: string;
}

interface EditValues {
  homeScore?: number;
  awayScore?: number;
  minute?: number;
  date?: Dayjs;
  venue?: string;
  ticketUrl?: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  presentingPartnerId?: string | null;
}

interface EventValues {
  type: MatchEventType;
  minute: number;
  team: 'home' | 'away';
  playerName: string;
  detail?: string;
}

function formatRome(iso: string): string {
  return dayjs(iso).tz(ROME_TZ).format('DD/MM/YYYY HH:mm');
}

export function MatchesPage(): React.ReactElement {
  const navigate = useNavigate();
  // T-93 — vista calendario: 'upcoming' (default) mostra dalla prossima
  // all'ultima e NASCONDE le concluse; 'finished' mostra solo le concluse
  // (più recente in cima); 'all' mostra tutto. Le concluse non sono eliminate,
  // solo dietro il filtro.
  const [view, setView] = useState<'upcoming' | 'finished' | 'all'>('upcoming');
  const [filterComp, setFilterComp] = useState<string>('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const [createForm] = Form.useForm<CreateValues>();
  const [editForm] = Form.useForm<EditValues>();
  const [eventForm] = Form.useForm<EventValues>();

  const {
    data: pageData,
    isLoading,
    refetch,
    isFetching,
  } = useMatches({
    concluded: view === 'all' ? undefined : view === 'finished',
    competition: filterComp || undefined,
    page,
    limit: 20,
  });
  const { data: editMatch } = useMatch(editId);
  const { data: drawerMatch } = useMatch(drawerId);
  const { data: nextMatch } = useNextMatch();
  const { data: partners = [] } = usePartners({ active: true });

  const createMutation = useCreateMatch();
  const updateMutation = useUpdateMatch();
  const deleteMutation = useDeleteMatch();
  const addEventMutation = useAddMatchEvent();

  const partnerOptions = [...partners]
    .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }))
    .map((p) => ({
      value: p.id,
      name: p.name,
      label: (
        <Space>
          <Avatar src={p.logoUrl} size={20} shape="square" />
          {p.name}
        </Space>
      ),
    }));

  const openCreate = (): void => {
    createForm.resetFields();
    createForm.setFieldsValue({
      isHome: true,
      competition: 'Serie C',
      date: dayjs().tz(ROME_TZ).hour(15).minute(0).second(0).millisecond(0),
    });
    setCreateOpen(true);
  };

  const openEdit = (m: MatchSummaryDto): void => {
    setEditId(m.id);
  };

  React.useEffect(() => {
    if (editId && editMatch) {
      editForm.setFieldsValue({
        homeScore: editMatch.homeScore ?? undefined,
        awayScore: editMatch.awayScore ?? undefined,
        minute: editMatch.minute ?? undefined,
        date: dayjs(editMatch.date).tz(ROME_TZ),
        venue: editMatch.venue,
        ticketUrl: editMatch.ticketUrl ?? undefined,
        homeTeamLogo: editMatch.homeTeamLogo ?? undefined,
        awayTeamLogo: editMatch.awayTeamLogo ?? undefined,
        presentingPartnerId: editMatch.presentingPartner?.id ?? null,
      });
    }
  }, [editId, editMatch, editForm]);

  const onCreate = async (): Promise<void> => {
    const v = await createForm.validateFields();
    try {
      await createMutation.mutateAsync({
        homeTeamName: v.homeTeamName,
        awayTeamName: v.awayTeamName,
        isHome: v.isHome,
        competition: v.competition,
        matchday: v.matchday,
        date: v.date.tz(ROME_TZ).second(0).millisecond(0).utc().toISOString(),
        venue: v.venue,
        ticketUrl: v.ticketUrl || undefined,
        homeTeamLogo: v.homeTeamLogo || undefined,
        awayTeamLogo: v.awayTeamLogo || undefined,
        presentingPartnerId: v.presentingPartnerId || undefined,
      });
      message.success('Partita creata');
      setCreateOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore creazione'));
    }
  };

  const onEdit = async (): Promise<void> => {
    if (!editId) return;
    const v = await editForm.validateFields();
    try {
      await updateMutation.mutateAsync({
        id: editId,
        input: {
          homeScore: v.homeScore,
          awayScore: v.awayScore,
          minute: v.minute,
          date: v.date ? v.date.tz(ROME_TZ).second(0).millisecond(0).utc().toISOString() : undefined,
          venue: v.venue,
          ticketUrl: v.ticketUrl || null,
          homeTeamLogo: v.homeTeamLogo,
          awayTeamLogo: v.awayTeamLogo,
          presentingPartnerId: v.presentingPartnerId === undefined ? undefined : v.presentingPartnerId,
        },
      });
      message.success('Partita aggiornata');
      setEditId(null);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Partita eliminata');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  const onAddEvent = async (): Promise<void> => {
    if (!drawerId) return;
    const v = await eventForm.validateFields();
    const input: CreateMatchEventInput = {
      type: v.type,
      minute: v.minute,
      team: v.team,
      playerName: v.playerName,
      detail: v.detail,
    };
    try {
      await addEventMutation.mutateAsync({ id: drawerId, input });
      message.success('Evento aggiunto');
      eventForm.resetFields();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore evento');
    }
  };

  const data = pageData?.data ?? [];
  const meta = pageData?.meta;

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Partite
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Segmented
            value={view}
            onChange={(v) => {
              setView(v as 'upcoming' | 'finished' | 'all');
              setPage(1);
            }}
            options={[
              { label: 'Prossime', value: 'upcoming' },
              { label: 'Concluse', value: 'finished' },
              { label: 'Tutte', value: 'all' },
            ]}
          />
          <Input
            placeholder="Competizione"
            allowClear
            style={{ width: 160 }}
            value={filterComp}
            onChange={(e) => {
              setFilterComp(e.target.value);
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuova partita
          </Button>
        </Space>
      }
    >
      {nextMatch ? (
        <Alert
          style={{ marginBottom: 16 }}
          type={nextMatch.ticketUrl ? 'info' : 'warning'}
          showIcon
          icon={<CalendarOutlined />}
          message={
            <Space size={8} wrap>
              <Typography.Text strong>Prossima partita:</Typography.Text>
              <Typography.Text>{formatRome(nextMatch.date)}</Typography.Text>
              <Typography.Text strong>
                {nextMatch.homeTeamName} vs {nextMatch.awayTeamName}
              </Typography.Text>
              <Tag color={STATUS_COLORS[nextMatch.status]}>{STATUS_LABELS[nextMatch.status]}</Tag>
              {nextMatch.ticketUrl ? (
                <Tag color="green">Biglietti: link presente</Tag>
              ) : (
                <Tag color="orange">Biglietti: nessun link</Tag>
              )}
            </Space>
          }
          action={
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(nextMatch)}>
              Modifica
            </Button>
          }
        />
      ) : null}

      <Table<MatchSummaryDto>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        onRow={(r) =>
          nextMatch && r.id === nextMatch.id
            ? { style: { background: '#e6f4ff' }, 'aria-label': 'Prossima partita' }
            : {}
        }
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        columns={[
          {
            title: 'Data (Roma)',
            dataIndex: 'date',
            width: 160,
            render: (v: string) => formatRome(v),
          },
          {
            title: 'Match',
            render: (_v, r) => (
              <Space direction="vertical" size={0}>
                <Space size={6}>
                  {r.homeTeamLogo ? <Avatar src={r.homeTeamLogo} size={20} shape="square" /> : null}
                  <Typography.Text strong>{r.homeTeamName}</Typography.Text>
                  <Typography.Text type="secondary">vs</Typography.Text>
                  <Typography.Text strong>{r.awayTeamName}</Typography.Text>
                  {r.awayTeamLogo ? <Avatar src={r.awayTeamLogo} size={20} shape="square" /> : null}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.competition}
                  {r.matchday ? ` · giornata ${r.matchday}` : ''}
                  {r.presentingPartner ? (
                    <>
                      {' · '}
                      <Tooltip title={`Match presented by: ${r.presentingPartner.name}`}>
                        <Tag color="blue" style={{ marginLeft: 4 }}>
                          <PictureOutlined /> {r.presentingPartner.name}
                        </Tag>
                      </Tooltip>
                    </>
                  ) : null}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: 'Score',
            width: 100,
            align: 'center',
            render: (_v, r) =>
              r.homeScore !== null && r.awayScore !== null ? (
                <Typography.Text strong>
                  {r.homeScore} - {r.awayScore}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Min',
            dataIndex: 'minute',
            width: 60,
            align: 'center',
            render: (v: number | null) => (v != null ? `${v}'` : '—'),
          },
          {
            title: 'Stato',
            dataIndex: 'status',
            width: 110,
            render: (s: MatchStatus) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>,
          },
          { title: 'Stadio', dataIndex: 'venue', width: 180 },
          {
            title: '',
            width: 200,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Tooltip title="Live dashboard">
                  <Button
                    size="small"
                    type={r.status === 'LIVE' ? 'primary' : 'default'}
                    danger={r.status === 'LIVE'}
                    icon={<PlayCircleOutlined />}
                    onClick={() => navigate(`/matches/${r.id}/live`)}
                  />
                </Tooltip>
                <Tooltip title="Eventi">
                  <Button
                    size="small"
                    icon={<ThunderboltOutlined />}
                    onClick={() => setDrawerId(r.id)}
                  />
                </Tooltip>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare partita?"
                  okText="Elimina"
                  cancelText="Annulla"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      {/* Create modal */}
      <Modal
        title="Nuova partita"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="Crea"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
        width={720}
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="homeTeamName"
              label="Squadra casa"
              rules={[{ required: true, max: 255 }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="awayTeamName"
              label="Squadra trasferta"
              rules={[{ required: true, max: 255 }]}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </Space.Compact>

          <Form.Item
            name="homeTeamLogo"
            label="Logo casa"
            tooltip="Seleziona dalla libreria media o carica nuovo"
          >
            <MediaOrUrlField category="TEAM_LOGO" placeholder="https://..." />
          </Form.Item>
          <Form.Item name="awayTeamLogo" label="Logo trasferta">
            <MediaOrUrlField category="TEAM_LOGO" placeholder="https://..." />
          </Form.Item>

          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="isHome"
              label="Tenant gioca in casa"
              rules={[{ required: true }]}
              style={{ width: 200, marginRight: 8 }}
            >
              <Select
                options={[
                  { value: true, label: 'Sì (casa)' },
                  { value: false, label: 'No (trasferta)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="competition"
              label="Competizione"
              rules={[{ required: true, max: 100 }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Input />
            </Form.Item>
            <Form.Item name="matchday" label="Giornata" style={{ width: 110 }}>
              <InputNumber min={1} max={50} style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name="date"
            label="Data e ora (Europe/Rome, HH:00/HH:30)"
            rules={[{ required: true, message: 'Data obbligatoria' }]}
            tooltip="Calendario in fuso orario Roma. Solo orari pieni HH:00 o HH:30."
          >
            <DatePicker
              showTime={{ format: 'HH:mm', minuteStep: 30, defaultValue: dayjs().hour(15).minute(0).second(0) }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              disabledTime={() => ({
                disabledMinutes: () =>
                  Array.from({ length: 60 }, (_, i) => i).filter((i) => i !== 0 && i !== 30),
                disabledSeconds: () =>
                  Array.from({ length: 60 }, (_, i) => i).filter((i) => i !== 0),
              })}
            />
          </Form.Item>
          <Form.Item name="venue" label="Stadio" rules={[{ required: true, max: 255 }]}>
            <Input placeholder="Stadio Gaetano Bonolis" />
          </Form.Item>
          <Form.Item
            name="ticketUrl"
            label="URL biglietti"
            tooltip="Se impostato, l'app mostra il bottone Acquista biglietti per questa partita"
            rules={[{ type: 'url', message: 'Inserisci un URL valido' }]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="presentingPartnerId"
            label="Match presented by (sponsor partita)"
            tooltip="Partner che presenta la partita; visibile lato app"
          >
            <Select
              allowClear
              showSearch
              filterOption={(input, opt) =>
                ((opt as { name?: string })?.name ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Seleziona partner..."
              options={partnerOptions}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit modal */}
      <Modal
        title="Modifica partita"
        open={editId !== null}
        onCancel={() => setEditId(null)}
        onOk={onEdit}
        okText="Salva"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
        width={640}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Lo stato della partita si gestisce dalla console live, non da qui.
          </Typography.Text>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="homeScore" label="Goal casa" style={{ flex: 1, marginRight: 8 }}>
              <InputNumber min={0} max={50} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="awayScore" label="Goal trasferta" style={{ flex: 1, marginRight: 8 }}>
              <InputNumber min={0} max={50} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="minute" label="Minuto" style={{ width: 110 }}>
              <InputNumber min={0} max={130} style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="date" label="Data e ora (Europe/Rome, HH:00/HH:30)">
            <DatePicker
              showTime={{ format: 'HH:mm', minuteStep: 30, defaultValue: dayjs().hour(15).minute(0).second(0) }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              disabledTime={() => ({
                disabledMinutes: () =>
                  Array.from({ length: 60 }, (_, i) => i).filter((i) => i !== 0 && i !== 30),
                disabledSeconds: () =>
                  Array.from({ length: 60 }, (_, i) => i).filter((i) => i !== 0),
              })}
            />
          </Form.Item>
          <Form.Item name="venue" label="Stadio">
            <Input />
          </Form.Item>
          <Form.Item
            name="ticketUrl"
            label="URL biglietti"
            tooltip="Se impostato, l'app mostra il bottone Acquista biglietti per questa partita"
            rules={[{ type: 'url', message: 'Inserisci un URL valido' }]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="homeTeamLogo" label="Logo casa">
            <MediaOrUrlField category="TEAM_LOGO" placeholder="https://..." />
          </Form.Item>
          <Form.Item name="awayTeamLogo" label="Logo trasferta">
            <MediaOrUrlField category="TEAM_LOGO" placeholder="https://..." />
          </Form.Item>
          <Form.Item name="presentingPartnerId" label="Match presented by">
            <Select
              allowClear
              showSearch
              filterOption={(input, opt) =>
                ((opt as { name?: string })?.name ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Seleziona partner..."
              options={partnerOptions}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Events drawer */}
      <Drawer
        title={
          drawerMatch
            ? `${drawerMatch.homeTeamName} vs ${drawerMatch.awayTeamName} — Eventi live`
            : 'Eventi'
        }
        placement="right"
        width={520}
        onClose={() => {
          setDrawerId(null);
          eventForm.resetFields();
        }}
        open={drawerId !== null}
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form form={eventForm} layout="vertical">
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="type"
                label="Tipo"
                rules={[{ required: true }]}
                style={{ width: 180, marginRight: 8 }}
              >
                <Select
                  options={(Object.keys(EVENT_LABELS) as MatchEventType[]).map((t) => ({
                    value: t,
                    label: EVENT_LABELS[t],
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="minute"
                label="Minuto"
                rules={[{ required: true }]}
                style={{ width: 100, marginRight: 8 }}
              >
                <InputNumber min={0} max={130} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="team"
                label="Squadra"
                rules={[{ required: true }]}
                style={{ width: 130 }}
              >
                <Select
                  options={[
                    { value: 'home', label: 'Casa' },
                    { value: 'away', label: 'Trasferta' },
                  ]}
                />
              </Form.Item>
            </Space.Compact>
            <Form.Item name="playerName" label="Giocatore" rules={[{ required: true, max: 200 }]}>
              <Input placeholder="Nome Cognome" />
            </Form.Item>
            <Form.Item name="detail" label="Dettaglio">
              <Input placeholder="Assist, descrizione..." />
            </Form.Item>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddEvent}
              loading={addEventMutation.isPending}
              block
            >
              Aggiungi evento
            </Button>
          </Form>
        </Card>

        <Typography.Title level={5}>
          <CalendarOutlined /> Cronologia
        </Typography.Title>
        {drawerMatch?.events.length ? (
          <Table
            dataSource={drawerMatch.events}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Min',
                dataIndex: 'minute',
                width: 60,
                render: (v: number) => `${v}'`,
              },
              {
                title: 'Tipo',
                dataIndex: 'type',
                width: 130,
                render: (v: MatchEventType) => EVENT_LABELS[v],
              },
              { title: 'Squadra', dataIndex: 'team', width: 90 },
              { title: 'Giocatore', dataIndex: 'playerName' },
            ]}
          />
        ) : (
          <Typography.Text type="secondary">Nessun evento.</Typography.Text>
        )}
      </Drawer>
    </Card>
  );
}

